import { Vec2, TAU, arcSweep, normalizeAngle } from './math';
import { nextId } from './ids';
import { intersectEntities } from './query';
import type { ArcEntity, CircleEntity, Entity, LineEntity, PolylineEntity } from './types';

/**
 * Splitting geometry at intersection points.
 *
 * Procedural modelling stores geometry as points and lets operations cut it: every entity
 * is sliced at its intersections with the others, so one line becomes several objects with
 * their own names instead of a single overlapping shape.
 */

const EPS = 1e-7;
/** How far a cut may sit off an edge and still count as being on it (mm). */
const ON_EDGE = 1e-6;
/** Curves come back from the intersection routine with a chord tolerance, so allow for it. */
const ON_ARC = 0.02;

function respawn<T extends Entity>(source: T, patch: Partial<T>): T {
  return { ...source, ...patch, id: nextId(source.type[0] ?? 'e') };
}

function dedupe(values: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const value of sorted) {
    if (out.length === 0 || Math.abs(value - out[out.length - 1]!) > EPS) out.push(value);
  }
  return out;
}

function splitLine(entity: LineEntity, cuts: readonly Vec2[]): Entity[] {
  const ab = entity.b.sub(entity.a);
  const lenSq = ab.lenSq();
  if (lenSq < 1e-18) return [entity];

  const params = [0, 1];
  for (const cut of cuts) {
    const t = cut.sub(entity.a).dot(ab) / lenSq;
    if (t <= EPS || t >= 1 - EPS) continue;
    // A cut only counts when it actually lies on this line ? projecting it blindly used to
    // invent splits for intersections that belong to some other edge.
    if (entity.a.add(ab.scale(t)).distanceTo(cut) > ON_EDGE) continue;
    params.push(t);
  }
  const stops = dedupe(params);
  if (stops.length <= 2) return [entity];

  const at = (t: number): Vec2 => entity.a.add(ab.scale(t));
  const pieces: Entity[] = [];
  for (let i = 0; i + 1 < stops.length; i++) {
    pieces.push(respawn(entity, { a: at(stops[i]!), b: at(stops[i + 1]!) }));
  }
  return pieces;
}

function splitPolyline(entity: PolylineEntity, cuts: readonly Vec2[]): Entity[] {
  // A closed polyline has an extra (closing) edge from the last vertex back to the first — it has
  // to be split like any other, otherwise the piece along it silently disappears.
  const ring = entity.closed ? [...entity.vertices, entity.vertices[0]!] : [...entity.vertices];
  const edgeCount = Math.max(0, ring.length - 1);
  const stopsByEdge: number[][] = Array.from({ length: edgeCount }, () => [0, 1]);

  for (const cut of cuts) {
    for (let i = 0; i < edgeCount; i++) {
      const a = ring[i]!;
      const b = ring[i + 1]!;
      const ab = b.sub(a);
      const lenSq = ab.lenSq();
      if (lenSq < 1e-18) continue;
      const t = cut.sub(a).dot(ab) / lenSq;
      if (t <= EPS || t >= 1 - EPS) continue;
      if (a.add(ab.scale(t)).distanceTo(cut) > ON_EDGE) continue;
      stopsByEdge[i]!.push(t);
    }
  }

  const pieces: Entity[] = [];
  let current: Vec2[] = [ring[0]!];

  // A cut that lands exactly on a corner divides the polyline just as an interior cut does. The
  // ends of the ring are already boundaries, so only the vertices in between count.
  const cornerCuts = new Set<number>();
  for (const cut of cuts) {
    for (let i = 1; i < edgeCount; i++) {
      if (ring[i]!.distanceTo(cut) <= ON_EDGE) cornerCuts.add(i);
    }
  }

  for (let i = 0; i < edgeCount; i++) {
    if (cornerCuts.has(i) && current.length > 1) {
      pieces.push(respawn(entity, { vertices: current, closed: false }));
      current = [ring[i]!];
    }
    const a = ring[i]!;
    const b = ring[i + 1]!;
    const stops = dedupe(stopsByEdge[i]!);
    for (let s = 1; s < stops.length; s++) {
      const point = a.lerp(b, stops[s]!);
      current.push(point);
      if (s < stops.length - 1) {
        pieces.push(respawn(entity, { vertices: current, closed: false }));
        current = [point];
      }
    }
  }
  pieces.push(respawn(entity, { vertices: current, closed: false }));
  const out = pieces.filter((piece) => (piece as PolylineEntity).vertices.length > 1);

  // A ring starts and ends at the same vertex: the piece either side of that seam is one piece —
  // unless the seam itself was cut, in which case those two pieces really are apart.
  const seamCut = cuts.some((cut) => ring[0]!.distanceTo(cut) <= ON_EDGE);
  if (entity.closed && !seamCut && out.length > 1) {
    const last = out.pop() as PolylineEntity;
    const first = out[0] as PolylineEntity;
    first.vertices = [...last.vertices, ...first.vertices.slice(1)];
  }
  // A closed run cut exactly once has no ends to trim between: split the seam run in half so the
  // loop still becomes two pieces that meet at the contact.
  if (entity.closed && out.length === 1) return splitLoopInHalf(entity, cuts);
  return out;
}

/** Last resort for a closed run with a single cut: two halves that meet at the cut. */
function splitLoopInHalf(entity: PolylineEntity, cuts: readonly Vec2[]): Entity[] {
  const ring = [...entity.vertices];
  const cut = cuts[0];
  if (!cut || ring.length < 4) return [entity];
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const distance = ring[i]!.distanceTo(cut);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  const half = Math.max(2, Math.round(ring.length / 2));
  const first: Vec2[] = [];
  const second: Vec2[] = [];
  for (let step = 0; step <= ring.length; step++) {
    const point = ring[(best + step) % ring.length]!;
    if (step < half) first.push(point);
    else second.push(point);
  }
  first.push(second[0] ?? ring[best]!);
  second.push(first[0]!);
  return [
    respawn(entity, { vertices: first, closed: false }),
    respawn(entity, { vertices: second, closed: false }),
  ];
}

function splitArc(entity: ArcEntity, cuts: readonly Vec2[]): Entity[] {  const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
  const direction = entity.ccw ? 1 : -1;
  const offsets = [0, sweep];

  for (const cut of cuts) {
    if (Math.abs(cut.distanceTo(entity.center) - entity.radius) > ON_ARC) continue;
    const angle = normalizeAngle(cut.sub(entity.center).angle());
    const along = entity.ccw
      ? normalizeAngle(angle - entity.startAngle)
      : normalizeAngle(entity.startAngle - angle);
    if (along > 1e-6 && along < sweep - 1e-6) offsets.push(along);
  }

  const stops = dedupe(offsets);
  if (stops.length <= 2) return [entity];

  const angleAt = (offset: number): number => entity.startAngle + direction * offset;
  const pieces: Entity[] = [];
  for (let i = 0; i + 1 < stops.length; i++) {
    pieces.push(
      respawn(entity, { startAngle: angleAt(stops[i]!), endAngle: angleAt(stops[i + 1]!) }),
    );
  }
  return pieces;
}

/** Splits one entity at the given points; returns the pieces (or the entity itself). */
export function splitEntityAt(entity: Entity, cuts: readonly Vec2[]): Entity[] {
  if (cuts.length === 0) return [entity];
  switch (entity.type) {
    case 'line':
      return splitLine(entity, cuts);
    case 'polyline':
      return splitPolyline(entity, cuts);
    case 'circle':
      return splitCircle(entity, cuts);
    case 'arc':
      return splitArc(entity, cuts);
    default:
      return [entity];
  }
}

/**
 * Cut angles closer than a hundredth of a millimetre of arc are one cut. A bore that only *touches*
 * another circle puts two crossings a hair apart, and the sliver between them is not a piece anyone
 * can draw, snap to or trim.
 */
function mergeNearAngles(angles: readonly number[], radius: number): number[] {
  const span = Math.max(EPS, 0.01 / Math.max(Math.abs(radius), 1e-9));
  const out: number[] = [];
  for (const angle of angles) {
    const previous = out[out.length - 1];
    if (previous !== undefined && Math.abs(angle - previous) <= span) continue;
    out.push(angle);
  }
  while (out.length > 1 && TAU - out[out.length - 1]! + out[0]! <= span) out.pop();
  return out;
}

/**
 * A circle has no ends, so cutting it turns it into arcs: every pair of neighbouring cuts becomes
 * one arc, including the pair that wraps across angle 0. Without this a circle could never be
 * trimmed where a line or a plate crosses it.
 */
function splitCircle(entity: CircleEntity, cuts: readonly Vec2[]): Entity[] {
  const angles: number[] = [];
  for (const cut of cuts) {
    if (Math.abs(cut.distanceTo(entity.center) - entity.radius) > ON_ARC) continue;
    angles.push(normalizeAngle(cut.sub(entity.center).angle()));
  }
  const stops = mergeNearAngles(dedupe(angles), entity.radius);
  if (stops.length < 2) {
    if (stops.length === 0) return [entity];
    // A loop cut exactly once has no ends to trim between — two shapes that merely *touch* land
    // here. Add the opposite point so the loop becomes two arcs meeting at the contact, which is
    // what makes a tangent circle trimmable at all.
    stops.push(normalizeAngle(stops[0]! + Math.PI));
  }

  const pieces: Entity[] = [];
  for (let i = 0; i < stops.length; i++) {
    const start = stops[i]!;
    let end = stops[(i + 1) % stops.length]!;
    if (end <= start) end += TAU;
    pieces.push({ ...entity, type: 'arc', startAngle: start, endAngle: end, ccw: true, id: nextId('a') });
  }
  // Two cuts a hair apart are one cut: a sliver of an arc is not a piece anyone can draw or trim.
  const real = pieces.filter((piece) => piece.type === 'arc' && arcSweep(piece.startAngle, piece.endAngle, piece.ccw) > 1e-9);
  return real.length > 0 ? real : [entity];
}

/** Intersection points of `entity` with the rest of the set. */
export function cutPointsFor(entity: Entity, others: readonly Entity[]): Vec2[] {
  const points: Vec2[] = [];
  for (const other of others) {
    if (other.id === entity.id) continue;
    for (const point of intersectEntities(entity, other, { chordTolerance: 0.01 })) {
      if (!points.some((existing) => existing.distanceTo(point) < 1e-7)) points.push(point);
    }
  }
  return points;
}

/**
 * Slices every entity of the set at its intersections with the others: overlapping or
 * crossing shapes become individual objects instead of staying whole.
 */
export function sliceAtIntersections(entities: readonly Entity[]): Entity[] {
  const out: Entity[] = [];
  for (const entity of entities) {
    const cuts = cutPointsFor(entity, entities);
    out.push(...splitEntityAt(entity, cuts));
  }
  return out;
}

/** Names pieces `BASE`, `BASE.2`, `BASE.3`… so procedural output stays identifiable. */
export function namePieces(base: string, pieces: readonly Entity[]): Entity[] {
  return pieces.map((piece, index) => ({
    ...piece,
    name: index === 0 ? base : `${base}.${index + 1}`,
  }));
}
